import { useGetLanguages } from '@/api/auth/languages/languages';
import type { LanguageResponse } from '@/api/auth/missionImpactAuthAPI.schemas';

/**
 * Default language code.
 */
export const DEFAULT_LANGUAGE = 'en';

/**
 * Fallback language used when API is unavailable.
 */
const FALLBACK_LANGUAGES: LanguageResponse[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
];

/**
 * Hook to get the list of supported languages from the API.
 * Falls back to a static list if the API is unavailable.
 */
export function useLanguages() {
  const { data, isLoading, error } = useGetLanguages({
    query: {
      staleTime: Infinity, // Languages rarely change, cache indefinitely
      gcTime: Infinity,
      retry: 1,
    },
  });

  const languages = data?.data ?? FALLBACK_LANGUAGES;

  return {
    languages,
    isLoading,
    error,
  };
}

/**
 * Get language display name by code.
 */
export function getLanguageName(languages: LanguageResponse[], code: string): string {
  const language = languages.find((lang) => lang.code === code);
  return language ? language.name : code;
}

/**
 * Get language native name by code.
 */
export function getLanguageNativeName(languages: LanguageResponse[], code: string): string {
  const language = languages.find((lang) => lang.code === code);
  return language ? language.nativeName : code;
}

/**
 * Check if a language code is supported.
 */
export function isLanguageSupported(languages: LanguageResponse[], code: string): boolean {
  return languages.some((lang) => lang.code === code);
}

/**
 * Get all supported language codes.
 */
export function getSupportedLanguageCodes(languages: LanguageResponse[]): string[] {
  return languages.map((lang) => lang.code);
}
